
/**
 * Module dependencies.
 */

import { Banner } from 'client/components/core/banner';
import { Container } from 'client/components/core/container';
import { Footer } from 'client/components/core/footer';
import { GetAppModal } from 'client/components/core/get-app-modal';
import { Loading, ModalPortal } from 'slyk-web-libs/design-system';
import { Navbar } from 'client/components/core/navbar';
import { Payspace, payspaceSignupModes } from 'slyk-web-libs/types';
import { ProductsList } from 'client/components/products/products-list';
import { dashboardUrlResolver } from 'client/core/utils/url-resolver';
import { formatCurrency } from 'client/core/utils/formatter';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { ifNotProp, prop, theme } from 'styled-tools';
import { map } from 'lodash';
import { useBanner } from 'client/hooks/use-banner';
import { useBreakpoint } from 'slyk-web-libs/breakpoint';
import { usePayspace } from 'slyk-web-libs/payspace-context';
import { useQueryString } from 'client/hooks/use-query-string';
import { useSlykApp } from 'client/hooks/use-slyk-app';
import { useTranslate } from 'slyk-web-libs/i18n';
import { useWindowScroll } from 'client/hooks/use-window-scroll';
import BigNumber from 'bignumber.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import routes from 'slyk-web-libs/config/dashboard-routes';
import styled, { css } from 'styled-components';

/**
 * Products list query.
 */

const productsListQuery = gql`
  query ProductsList {
    categories(
      all: true
      filter: [{
        name: "visibleProducts",
        value: true
      }]
      sort: "order"
    ) {
      data {
        description
        id
        title
      }
    }

    defaultCurrency {
      assetCode: code
      decimalPlaces
      decimalPlacesToDisplay
      skipTrailingZeros
      symbol
    }

    includeTaxOnProductPrice: settingByCode(
      code: "includeTaxOnProductPrice"
    ) {
      value
    }

    products(
      all: true
      filter: [{
        name: "visible",
        value: false
      }, {
        name: "typeCode",
        value: "nin:fund_raise"
      }]
      sort: "order"
    ) {
      data {
        available
        id
        location
        name
        price
        priceWithTax
        taxRateId
        thumbnail {
          ... on Image {
            status
            type
            url
          }
          ... on Video {
            status
            thumbnailUrl
            type
            url
          }
        }
        unitOfAccount
      }
      total
    }

    rewardAsset: defaultBonusAsset {
      assetCode: code
      baseRates {
        quoteAssetCode
        rate
      }
      decimalPlaces
      decimalPlacesToDisplay
      skipTrailingZeros
      symbol
      type
    }
  }
`;

/**
 * Products list lazy query.
 */

const productsListLazyQuery = gql`
  query ProductsListLazyQuery($categoryId: FilterValueType) {
    products(
      all: true
      filter: [{
        name: "categoryId",
        value: $categoryId
      }]
      sort: "order"
    ) {
      data {
        available
        id
        location
        name
        price
        priceWithTax
        taxRateId
        thumbnail {
          ... on Image {
            status
            type
            url
          }
          ... on Video {
            status
            thumbnailUrl
            type
            url
          }
        }
        unitOfAccount
      }
      total
    }
  }
`;

/**
 * `LoadingContainer` styled component.
 */

const LoadingContainer = styled(Container)`
  display: grid;
  grid-template-rows: 1fr;
  left: 50%;
  padding-top: ${prop('navbarHeight', 122)}px;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: transform ${theme('animations.defaultTransition')};
`;

/**
 * `StyledLoading` styled component.
 */

const StyledLoading = styled(Loading)`
  grid-column: 1 / -1;
  grid-row: 1;
  place-self: center;
`;

/**
 * `SectionWrapper` styled component.
 */

const SectionWrapper = styled.div<{ isVisible: boolean; navbarHeight: number }>`
  padding-top: ${prop('navbarHeight', 122)}px;

  ${ifNotProp('isVisible', css`
    display: none;
  `)}
`;

/**
 * Export `BaseProductsList` container.
 */

export function BaseProductsList(): JSX.Element {
  const { i18n, translate } = useTranslate();
  const locale = i18n.language;
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [navbarHeight, setNavbarHeight] = useState<number>(0);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const { name, signupMode, slug } = usePayspace() as Payspace;
  const closeModal = useCallback(() => setModalOpen(false), []);
  const { code } = useQueryString() ?? {};
  const isMobile = useBreakpoint('ms', 'max');
  const isPublicPayspace = signupMode === payspaceSignupModes.public;
  const { data, loading: isLoadingData } = useQuery(productsListQuery, {
    context: {
      headers: {
        'x-payspace-slug': slug
      }
    },
    skip: !isPublicPayspace
  });

  const [
    fetchProductsByCategory,
    {
      data: productsByCategoryData,
      loading: isLoadingProductsByCategory
    }
  ] = useLazyQuery(productsListLazyQuery, {
    context: {
      headers: {
        'x-payspace-slug': slug
      }
    }
  });

  const categories = useMemo(() => {
    return data?.categories?.data ?? [];
  }, [data]);

  const products = useMemo(() => {
    return !activeCategoryId ? data?.products?.data : productsByCategoryData?.products?.data;
  }, [activeCategoryId, data, productsByCategoryData]);

  const normalizedProducts = useMemo(() => {
    return map(products ?? [], ({ price, priceWithTax, taxRateId, ...rest }) => {
      const hasTaxIncluded = taxRateId && data?.includeTaxOnProductPrice?.value;
      const priceAmount = new BigNumber(hasTaxIncluded && priceWithTax ? priceWithTax : price);
      const defaultCurrency = data?.defaultCurrency;
      const rate = data?.rewardAsset?.baseRates?.find((rate: any) => {
        return rate?.quoteAssetCode === defaultCurrency?.assetCode;
      })?.rate;

      const displayPrice = new BigNumber(priceAmount).isZero() ? translate('products.free') : formatCurrency(priceAmount, {
        ...defaultCurrency,
        locale
      });

      const hasPriceConverted = rate && price && priceAmount.isGreaterThan(0);
      const displayPriceConverted = hasPriceConverted ? formatCurrency(priceAmount.dividedBy(rate), {
        ...data?.rewardAsset,
        locale
      }) : null;

      return {
        ...rest,
        displayPrice,
        displayPriceConverted,
        price
      };
    });
  }, [data, locale, products, translate]);

  const handleActiveCategoryId = useCallback(async (id: string | null) => {
    setActiveCategoryId(id);
    await fetchProductsByCategory({ variables: { categoryId: id } });
  }, [fetchProductsByCategory]);

  const {
    canDownload,
    downloadAppUrl,
    getSlykApp
  } = useSlykApp({
    code: code as string | null | undefined,
    onOpenModal: () => setModalOpen(true),
    slug
  });

  const { isNavbarFixed } = useWindowScroll();
  const { isBannerVisible, onClose } = useBanner();

  useEffect(() => {
    function handleResize() {
      const navbarHeight = document.querySelector('nav')?.offsetHeight ?? 0;

      setNavbarHeight(navbarHeight);
    }

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Banner
        goToDashboard={dashboardUrlResolver(slug, routes.overview)}
        isVisible={isBannerVisible}
        onDismiss={onClose}
      />

      <Navbar
        downloadAppDisabled={isLoadingData}
        isFixed={isNavbarFixed}
        onGetApp={getSlykApp}
        showBadges={!isMobile}
      />

      {!!isLoadingData && (
        <LoadingContainer navbarHeight={navbarHeight}>
          <StyledLoading
            active
            relative
          />
        </LoadingContainer>
      )}

      <SectionWrapper
        isVisible={!isLoadingData}
        navbarHeight={navbarHeight}
      >
        <ProductsList
          activeCategoryId={activeCategoryId}
          categories={categories}
          isLoading={isLoadingProductsByCategory}
          onActiveCategoryId={handleActiveCategoryId}
          products={normalizedProducts}
        />

        <Footer
          getSlykApp={getSlykApp}
          payspaceName={name}
          payspaceSlug={slug}
        />
      </SectionWrapper>

      {!canDownload && (
        <ModalPortal isOpen={isModalOpen}>
          <GetAppModal
            code={code as string | null | undefined}
            downloadAppUrl={downloadAppUrl}
            isVisible={isModalOpen}
            onRequestClose={closeModal}
          />
        </ModalPortal>
      )}
    </>
  );
}
